import React from 'react';
import { Controller } from 'react-hook-form';
import {
    DocumentCombobox,
    DocumentCopyInput,
    DocumentDateInput,
    DocumentInput,
    DocumentNumberInput,
    DocumentRadioGroup,
    DocumentSelect,
    DocumentTextarea,
    DocumentRepeater,
    DocumentRichText,
    DocumentRichTextEnhanced,
} from '../form/inputs';
import { InputNode } from '../types';
import { DocumentRendererContext } from './types';
import { DocumentActionRenderer } from './DocumentActionRenderer'; // Import DocumentActionRenderer
import {
    createEvaluateCondition,
    executeUpdateRules,
} from '../utils/formUtils';
import type { DocumentRichTextRef } from '../form/inputs/DocumentRichText';

interface DocumentInputRendererProps {
    field: InputNode;
    context: DocumentRendererContext;
    value?: any;
    onChange?: (value: any) => void;
    /** Fires only on explicit commits (option selection, Enter, blur).
     *  Useful for repeater combo-add mode where every keystroke should
     *  NOT add an item. */
    onCommit?: (value: any) => void;
    onContextUpdate?: (updates: Record<string, any>) => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    onLoadingChange?: (loading: boolean) => void;
}

export const DocumentInputRenderer: React.FC<DocumentInputRendererProps> = ({
    field,
    context,
    value: manualValue,
    onChange: manualOnChange,
    onCommit,
    onContextUpdate,
    onKeyDown,
    onLoadingChange,
}) => {
    const richTextRef = React.useRef<DocumentRichTextRef | null>(null);

    const {
        control,
        watch,
        updateData,
        dataSources,
        checkDisable,
        checkHidden,
        contextData,
        setValue,
        variableOptions = [],
    } = context;

    const isConditionallyDisabled = checkDisable(field.disableWhen);
    const isReadOnly = field.readonly || isConditionallyDisabled;
    const shouldRenderCopyInput =
        field.inputType === 'text' &&
        field.readonly === true &&
        field.copyable === true &&
        !isConditionallyDisabled;
    const evaluateCondition = createEvaluateCondition(watch, contextData);

    const handleUpdates = (newValue: any, oldValue: any) => {
        executeUpdateRules(field.updates, {
            watch,
            setValue,
            updateData,
            evaluateCondition,
            localContext: {
                value: newValue,
                previousValue: oldValue,
            },
        });
    };

    // Initial seeded context should win for both new and existing documents.
    const getDefaultValue = (fallback: any = '') => {
        if (contextData[field.id] !== undefined) {
            return contextData[field.id];
        }
        if (field.data?.default?.fixed !== undefined) {
            return field.data.default.fixed;
        }
        if (field.data?.default?.source) {
            const sourceValue = watch(field.data.default.source);
            if (sourceValue !== undefined) {
                return sourceValue;
            }
        }
        return fallback;
    };

    const commonProps = {
        placeholder: field.placeholder,
        disabled: isReadOnly,
    };

    // Shared addOn wrapper (action buttons like importFromSchema attached to
    // a field). The textarea case keeps its own historical inline handling;
    // text/richtext cases wrap their element with this.
    const wrapWithAddOns = (element: React.ReactNode): React.ReactNode => {
        const addOns = field.addOns || [];
        if (addOns.length === 0) return element;

        const beforeAddOns = addOns.filter((a) => a.position === 'before');
        const afterAddOns = addOns.filter(
            (a) => !a.position || a.position === 'after'
        );
        const allButtons = (list: typeof addOns) =>
            list.length > 0 &&
            list.every(
                (addOn) =>
                    addOn.kind === 'action' && addOn.actionType === 'button'
            );

        const renderAddOn = (addOn: any) => {
            if (addOn.kind !== 'action') return null;
            const invokeAction = async (actionId: string) => {
                const handler = context.functions.actions[actionId];
                if (!handler) return;
                await handler({
                    action: addOn,
                    watch,
                    setValue,
                    updateData,
                    data: contextData,
                });
            };
            return (
                <div key={addOn.id}>
                    <DocumentActionRenderer
                        field={addOn}
                        context={context}
                        onSuccess={() => {}}
                        handleCopyNote={async () => invokeAction('copyNote')}
                        handlePreview={() => {
                            void invokeAction('preview');
                        }}
                        handleSave={async () => invokeAction('saveDraft')}
                        handleComplete={async () =>
                            invokeAction('completeReport')
                        }
                        postActionDialog={{
                            open: false,
                            config: undefined,
                            isSuccess: true,
                        }}
                        setPostActionDialog={() => {}}
                    />
                </div>
            );
        };

        return (
            <div className="w-full">
                {beforeAddOns.length > 0 && (
                    <div
                        className={
                            allButtons(beforeAddOns)
                                ? 'mb-2 flex flex-wrap gap-2'
                                : 'mb-2'
                        }
                    >
                        {beforeAddOns.map(renderAddOn)}
                    </div>
                )}
                {element}
                {afterAddOns.length > 0 && (
                    <div
                        className={
                            allButtons(afterAddOns)
                                ? 'mt-2 flex flex-wrap gap-2'
                                : 'mt-2'
                        }
                    >
                        {afterAddOns.map(renderAddOn)}
                    </div>
                )}
            </div>
        );
    };

    switch (field.inputType) {
        case 'search-text': {
            const sourceName = field.data?.options?.source;
            const fixedOptions = field.data?.options?.fixed;

            const sourceOptions = sourceName
                ? dataSources[sourceName]?.map((item: any) =>
                      typeof item === 'string' ? item : item.label || item.value
                  ) || []
                : [];

            const fixedOptionLabels = fixedOptions?.map((o) => o.label) || [];

            // When no source, pass fixed as main options (cmdk filters them)
            // When source exists, pass source as main and fixed as secondary
            const comboboxOptions = sourceName
                ? sourceOptions
                : fixedOptionLabels;

            const searchProviderKey = field.searchProviderKey || sourceName;
            const searchProvider =
                context.functions.searchProviders?.[searchProviderKey || ''];
            const showSearch = !!searchProvider;
            const inputHandlerKey =
                field.functionKey ||
                (sourceName === 'implantSuggestions'
                    ? 'implantConditionals'
                    : undefined);

            const runInputHandler = async (option: any, value: any) => {
                if (!inputHandlerKey) return;
                const inputHandler =
                    context.functions.inputHandlers[inputHandlerKey];
                if (!inputHandler) return;
                onLoadingChange?.(true);
                try {
                    await inputHandler({
                        field,
                        value,
                        option,
                        updateData,
                        setValue,
                        contextData,
                        onContextUpdate,
                    });
                } catch (e) {
                    console.error('Failed to run input handler', e);
                } finally {
                    onLoadingChange?.(false);
                }
            };

            const renderComponent = ({ onChange, value }: any) => (
                <DocumentCombobox
                    options={comboboxOptions}
                    fixedOptions={sourceName ? fixedOptionLabels : undefined}
                    sourceLabel={sourceName ? 'Previous' : undefined}
                    value={value}
                    onChange={async (val, option) => {
                        onChange(val);
                        if (!manualOnChange) updateData(field.id, val);
                        await runInputHandler(option, val);
                    }}
                    onCommit={onCommit}
                    showSearch={showSearch}
                    searchProvider={searchProvider}
                    selectOnlyOnEnter={true}
                    onKeyDown={async (e: React.KeyboardEvent, option?: any) => {
                        if (e.key === 'Enter' && option) {
                            await runInputHandler(option, manualValue);
                        }
                        onKeyDown?.(e);
                    }}
                    {...commonProps}
                />
            );

            if (manualOnChange) {
                return renderComponent({
                    onChange: manualOnChange,
                    value: manualValue,
                });
            }

            return (
                <Controller
                    name={field.id}
                    control={control}
                    defaultValue={getDefaultValue('')}
                    render={({ field: { onChange, value } }) =>
                        renderComponent({ onChange, value })
                    }
                />
            );
        }

        case 'text':
            if (shouldRenderCopyInput) {
                const renderCopyInput = ({ value }: any) => (
                    <DocumentCopyInput
                        value={value}
                        readOnly
                        {...commonProps}
                    />
                );

                if (manualOnChange) {
                    return renderCopyInput({ value: manualValue || '' });
                }

                return (
                    <Controller
                        name={field.id}
                        control={control}
                        defaultValue={getDefaultValue('')}
                        render={({ field: { value } }) =>
                            renderCopyInput({ value })
                        }
                    />
                );
            }

            const renderTextInput = ({ onChange, value, ref }: any) => (
                <DocumentInput
                    value={value}
                    onChange={(e) => {
                        onChange(e);
                        if (manualOnChange) {
                            manualOnChange(e.target.value);
                        } else {
                            updateData(field.id, e.target.value);
                        }
                    }}
                    ref={ref}
                    onKeyDown={onKeyDown}
                    {...commonProps}
                />
            );

            if (manualOnChange) {
                return wrapWithAddOns(
                    renderTextInput({
                        onChange: () => {}, // Handled inside component wrapper logic above for simplicity
                        value: manualValue || '',
                    })
                );
            }

            return wrapWithAddOns(
                <Controller
                    name={field.id}
                    control={control}
                    defaultValue={getDefaultValue('')}
                    render={({ field: { onChange, value, ref } }) =>
                        renderTextInput({ onChange, value, ref })
                    }
                />
            );

        case 'date':
            const renderDateInput = ({ onChange, value, ref }: any) => (
                <DocumentDateInput
                    value={value}
                    onChange={(e) => {
                        onChange(e);
                        if (manualOnChange) {
                            manualOnChange(e.target.value);
                        } else {
                            updateData(field.id, e.target.value);
                        }
                    }}
                    ref={ref}
                    onKeyDown={onKeyDown}
                    {...commonProps}
                />
            );

            if (manualOnChange) {
                return renderDateInput({
                    onChange: () => {},
                    value: manualValue || '',
                });
            }

            return (
                <Controller
                    name={field.id}
                    control={control}
                    defaultValue={getDefaultValue('')}
                    render={({ field: { onChange, value, ref } }) =>
                        renderDateInput({ onChange, value, ref })
                    }
                />
            );

        case 'repeater':
            // Repeater generally doesn't support manual overrides yet as it's complex
            // But we pass context to it now!
            return (
                <Controller
                    name={field.id}
                    control={control}
                    defaultValue={getDefaultValue([])}
                    render={({ field: { onChange, value } }) => (
                        <DocumentRepeater
                            field={field}
                            value={value}
                            onChange={(val) => {
                                // Capture previous value for update logic
                                const prevValue = value;
                                onChange(val);
                                updateData(field.id, val);
                                handleUpdates(val, prevValue);
                            }}
                            checkHidden={checkHidden}
                            disabled={field.readonly}
                            context={context}
                        />
                    )}
                />
            );

        case 'custom': {
            const CustomRenderer =
                context.functions.customInputs?.[field.functionKey!];
            if (!CustomRenderer) {
                return (
                    <div className="text-sm text-muted-foreground">
                        Unknown custom input: {field.functionKey}
                    </div>
                );
            }

            const renderCustomInput = ({ onChange, value }: any) => (
                <CustomRenderer
                    field={field}
                    value={value}
                    onChange={(nextValue) => {
                        onChange(nextValue);
                        if (manualOnChange) {
                            manualOnChange(nextValue);
                        } else {
                            updateData(field.id, nextValue);
                        }
                    }}
                    disabled={isReadOnly}
                />
            );

            if (manualOnChange) {
                return renderCustomInput({
                    onChange: () => {},
                    value: manualValue,
                });
            }

            return (
                <Controller
                    name={field.id}
                    control={control}
                    defaultValue={getDefaultValue(undefined)}
                    render={({ field: { onChange, value } }) =>
                        renderCustomInput({ onChange, value })
                    }
                />
            );
        }

        case 'select': {
            let selectOptions: { label: string; value: string }[] = [];
            const selectSource = field.data?.options?.source;
            const selectFixed = field.data?.options?.fixed;

            if (selectSource && dataSources[selectSource]) {
                selectOptions = dataSources[selectSource].map((item) =>
                    typeof item === 'string'
                        ? { label: item, value: item }
                        : item
                );
            } else if (selectFixed) {
                selectOptions = selectFixed;
            }

            const renderSelect = ({ onChange, value }: any) => (
                <DocumentSelect
                    options={selectOptions}
                    value={value}
                    onChange={(val) => {
                        onChange(val);
                        if (manualOnChange) {
                            manualOnChange(val);
                        } else {
                            updateData(field.id, val);
                        }
                    }}
                    {...commonProps}
                />
            );

            if (manualOnChange) {
                return renderSelect({
                    onChange: () => {},
                    value: manualValue || '',
                });
            }

            return (
                <Controller
                    name={field.id}
                    control={control}
                    defaultValue={getDefaultValue('')}
                    render={({ field: { onChange, value } }) =>
                        renderSelect({ onChange, value })
                    }
                />
            );
        }

        case 'textarea': {
            const addOns = field.addOns || [];
            const beforeAddOns = addOns.filter((a) => a.position === 'before');
            const afterAddOns = addOns.filter(
                (a) => !a.position || a.position === 'after'
            );

            const renderAddOn = (addOn: any) => {
                if (addOn.kind === 'action') {
                    const invokeAction = async (actionId: string) => {
                        const handler = context.functions.actions[actionId];
                        if (!handler) return;
                        await handler({
                            action: addOn,
                            watch,
                            setValue,
                            updateData,
                            data: contextData,
                        });
                    };
                    return (
                        <div key={addOn.id}>
                            <DocumentActionRenderer
                                field={addOn}
                                context={context}
                                onSuccess={() => {}}
                                handleCopyNote={async () =>
                                    invokeAction('copyNote')
                                }
                                handlePreview={() => {
                                    void invokeAction('preview');
                                }}
                                handleSave={async () =>
                                    invokeAction('saveDraft')
                                }
                                handleComplete={async () =>
                                    invokeAction('completeReport')
                                }
                                postActionDialog={{
                                    open: false,
                                    config: undefined,
                                    isSuccess: true,
                                }}
                                setPostActionDialog={() => {}}
                            />
                        </div>
                    );
                }
                return null;
            };

            const allBeforeAddOnsAreButtons =
                beforeAddOns.length > 0 &&
                beforeAddOns.every(
                    (addOn) =>
                        addOn.kind === 'action' && addOn.actionType === 'button'
                );
            const allAfterAddOnsAreButtons =
                afterAddOns.length > 0 &&
                afterAddOns.every(
                    (addOn) =>
                        addOn.kind === 'action' && addOn.actionType === 'button'
                );

            const renderTextarea = ({ onChange, value, ref }: any) => (
                <DocumentTextarea
                    value={value}
                    onChange={(e) => {
                        onChange(e);
                        if (manualOnChange) {
                            manualOnChange(e.target.value);
                        } else {
                            updateData(field.id, e.target.value);
                        }
                    }}
                    ref={ref}
                    {...commonProps}
                />
            );

            if (manualOnChange) {
                return (
                    <div className="w-full">
                        {beforeAddOns.length > 0 && (
                            <div
                                className={
                                    allBeforeAddOnsAreButtons
                                        ? 'mb-2 flex flex-wrap gap-2'
                                        : 'mb-2'
                                }
                            >
                                {beforeAddOns.map(renderAddOn)}
                            </div>
                        )}
                        {renderTextarea({
                            onChange: () => {},
                            value: manualValue || '',
                        })}
                        {afterAddOns.length > 0 && (
                            <div
                                className={
                                    allAfterAddOnsAreButtons
                                        ? 'mt-2 flex flex-wrap gap-2'
                                        : 'mt-2'
                                }
                            >
                                {afterAddOns.map(renderAddOn)}
                            </div>
                        )}
                    </div>
                );
            }

            return (
                <div className="w-full">
                    {beforeAddOns.length > 0 && (
                        <div
                            className={
                                allBeforeAddOnsAreButtons
                                    ? 'mb-2 flex flex-wrap gap-2'
                                    : 'mb-2'
                            }
                        >
                            {beforeAddOns.map(renderAddOn)}
                        </div>
                    )}
                    <Controller
                        name={field.id}
                        control={control}
                        defaultValue={getDefaultValue('')}
                        render={({ field: { onChange, value, ref } }) =>
                            renderTextarea({ onChange, value, ref })
                        }
                    />
                    {afterAddOns.length > 0 && (
                        <div
                            className={
                                allAfterAddOnsAreButtons
                                    ? 'mt-2 flex flex-wrap gap-2'
                                    : 'mt-2'
                            }
                        >
                            {afterAddOns.map(renderAddOn)}
                        </div>
                    )}
                </div>
            );
        }

        case 'richtext': {
            const renderRichText = ({ onChange, value, ref }: any) => (
                <DocumentRichText
                    value={value}
                    onChange={(val) => {
                        onChange(val);
                        if (manualOnChange) {
                            manualOnChange(val);
                        } else {
                            updateData(field.id, val);
                        }
                    }}
                    ref={(node) => {
                        richTextRef.current = node;
                        if (typeof ref === 'function') ref(node);
                        else if (ref) ref.current = node;
                    }}
                    extensions={(field.extensions || [])
                        .map((key) =>
                            context.functions.richTextExtensions?.[key]?.()
                        )
                        .filter(Boolean)}
                    {...commonProps}
                />
            );

            if (manualOnChange) {
                return wrapWithAddOns(
                    renderRichText({
                        onChange: () => {},
                        value: manualValue || '',
                    })
                );
            }

            return wrapWithAddOns(
                <Controller
                    name={field.id}
                    control={control}
                    defaultValue={getDefaultValue('')}
                    render={({ field: { onChange, value, ref } }) =>
                        renderRichText({ onChange, value, ref })
                    }
                />
            );
        }

        case 'richtext-enhanced': {
            const renderEnhancedRichText = ({ onChange, value, ref }: any) => (
                <DocumentRichTextEnhanced
                    value={value}
                    onChange={(val) => {
                        onChange(val);
                        if (manualOnChange) {
                            manualOnChange(val);
                        } else {
                            updateData(field.id, val);
                        }
                    }}
                    ref={(node) => {
                        richTextRef.current = node;
                        if (typeof ref === 'function') ref(node);
                        else if (ref) ref.current = node;
                    }}
                    extensions={(field.extensions || [])
                        .map((key) =>
                            context.functions.richTextExtensions?.[key]?.()
                        )
                        .filter(Boolean)}
                    variableOptions={variableOptions}
                    {...commonProps}
                />
            );

            if (manualOnChange) {
                return renderEnhancedRichText({
                    onChange: () => {},
                    value: manualValue || '',
                });
            }

            return (
                <Controller
                    name={field.id}
                    control={control}
                    defaultValue={getDefaultValue('')}
                    render={({ field: { onChange, value, ref } }) =>
                        renderEnhancedRichText({ onChange, value, ref })
                    }
                />
            );
        }

        case 'radio': {
            const renderRadio = ({ onChange, value }: any) => (
                <DocumentRadioGroup
                    options={field.data?.options?.fixed || []}
                    value={value}
                    onChange={(val) => {
                        onChange(val);
                        if (manualOnChange) {
                            manualOnChange(val);
                        } else {
                            updateData(field.id, val);
                        }
                    }}
                    name={field.id}
                    {...commonProps}
                />
            );

            if (manualOnChange) {
                return renderRadio({
                    onChange: () => {},
                    value: manualValue || '',
                });
            }

            return (
                <Controller
                    name={field.id}
                    control={control}
                    defaultValue={getDefaultValue('')}
                    render={({ field: { onChange, value } }) =>
                        renderRadio({ onChange, value })
                    }
                />
            );
        }

        case 'number':
            const renderNumberInput = ({ onChange, value, ref }: any) => (
                <DocumentNumberInput
                    value={value}
                    onChange={(e) => {
                        onChange(e);
                        if (manualOnChange) {
                            manualOnChange(e.target.value);
                        } else {
                            updateData(field.id, e.target.value);
                        }
                    }}
                    ref={ref}
                    onKeyDown={onKeyDown}
                    {...commonProps}
                />
            );

            if (manualOnChange) {
                return renderNumberInput({
                    onChange: () => {},
                    value: manualValue || '',
                });
            }

            return (
                <Controller
                    name={field.id}
                    control={control}
                    defaultValue={getDefaultValue('')}
                    render={({ field: { onChange, value, ref } }) =>
                        renderNumberInput({ onChange, value, ref })
                    }
                />
            );

        case 'tag-list':
            return (
                <Controller
                    name={field.id}
                    control={control}
                    defaultValue={getDefaultValue('')}
                    render={({ field: { onChange, value, ref } }) => (
                        <DocumentInput
                            value={value}
                            onChange={(e) => {
                                onChange(e);
                                updateData(field.id, e.target.value);
                            }}
                            ref={ref}
                            placeholder={`${
                                field.placeholder || ''
                            } (Tag list not implemented)`}
                            {...commonProps}
                        />
                    )}
                />
            );

        default:
            return (
                <Controller
                    name={field.id}
                    control={control}
                    defaultValue={getDefaultValue('')}
                    render={({ field: { onChange, value, ref } }) => (
                        <DocumentInput
                            value={value}
                            onChange={(e) => {
                                onChange(e);
                                updateData(field.id, e.target.value);
                            }}
                            ref={ref}
                            placeholder={`Unsupported type: ${field.inputType}`}
                            {...commonProps}
                        />
                    )}
                />
            );
    }
};
